/**
 * Toast context, types and the `useToast()` hook (#2059, EPIC #2048 Tier 1.5).
 *
 * Split out of Toast.tsx so that the .tsx module exports ONLY components,
 * keeping React Fast Refresh + the `react-refresh/only-export-components` lint
 * rule clean -- mirrors the contexts/auth.ts <-> AuthContext.tsx split.
 *
 * See Toast.tsx for the full design rationale (the audit's #1 interaction fix:
 * one accessible, token-driven, reduced-motion-safe global feedback layer).
 */
import { createContext, useContext } from "react";
import type { ReactNode } from "react";

export type ToastVariant = "success" | "error" | "info";

export interface ToastOptions {
  /** Optional bold heading above the message line. */
  title?: string;
  /**
   * When true, the toast shows a thin countdown line sweeping along the accent
   * border for its auto-dismiss `duration` -- the visible "time left to Undo"
   * affordance. Set by `toast.undo`; not usually passed by hand. Motion-safe
   * (a reduced-motion user sees a static full bar, no movement).
   */
  countdown?: boolean;
  /**
   * Auto-dismiss delay in ms. Defaults to 4000 for success/info and 7000 for
   * error (errors are read more slowly). Pass 0 to make the toast persist until
   * the user dismisses it manually.
   */
  duration?: number;
  /**
   * Optional single inline action (e.g. "Undo"). Rendered as a focusable
   * button; clicking it runs `onClick` then dismisses the toast.
   */
  action?: { label: string; onClick: () => void };
}

export interface ToastSpec extends ToastOptions {
  variant: ToastVariant;
  message: ReactNode;
}

export interface ToastApi {
  /** Low-level: show a toast of any variant; returns its id. */
  show: (spec: ToastSpec) => number;
  success: (message: ReactNode, options?: ToastOptions) => number;
  error: (message: ReactNode, options?: ToastOptions) => number;
  info: (message: ReactNode, options?: ToastOptions) => number;
  /**
   * A reversible-action toast (#2580, Tier 5). Info-styled, with an "Undo"
   * action button and a visible countdown affordance (a thin progress line
   * sweeping along the accent border). Defaults to ~6s (longer than a plain
   * info toast so the reversal window is real). `onUndo` runs on click, then
   * the toast dismisses; if it auto-dismisses the action simply never fires.
   *
   * ONLY attach this to genuinely reversible actions (e.g. an archive with an
   * existing restore endpoint) -- never to a non-reversible one.
   */
  undo: (
    message: ReactNode,
    onUndo: () => void,
    options?: Pick<ToastOptions, "duration" | "title">,
  ) => number;
  /** Dismiss a specific toast by id. */
  dismiss: (id: number) => void;
}

export const ToastContext = createContext<ToastApi | null>(null);

/**
 * A safe no-op toast API used when `useToast()` is called with no
 * <ToastProvider> mounted above it. This makes a toast call a HARMLESS no-op in
 * that case (e.g. a component rendered standalone in a unit test) instead of
 * crashing the surface -- feedback is additive, so its absence must never break
 * the underlying action. In the real app the provider is mounted once in
 * App.tsx, so every CRM surface gets real toasts.
 */
const NOOP_TOAST: ToastApi = {
  show: () => -1,
  success: () => -1,
  error: () => -1,
  info: () => -1,
  undo: () => -1,
  dismiss: () => {},
};

/**
 * Access the global toast API. In the app this resolves to the live API from
 * the <ToastProvider> mounted in App.tsx; with no provider above it (e.g. a
 * standalone unit test) it resolves to a no-op so a toast call never crashes
 * the host surface.
 */
export function useToast(): ToastApi {
  return useContext(ToastContext) ?? NOOP_TOAST;
}
